import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, ArrowLeft, Receipt, NotePencil, CheckCircle, Warning, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtCur = (v) => `Rs ${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const STATUS_FLOW = ['Draft', 'Recorded', 'Linked', 'Reconciled'];
const STATUS_COLORS = {
  'Draft': 'bg-zinc-100 text-zinc-600',
  'Recorded': 'bg-blue-100 text-blue-700',
  'Linked': 'bg-amber-100 text-amber-700',
  'Reconciled': 'bg-green-100 text-green-700',
};

const CreditDebitNoteRegister = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState(searchParams.get('project') || '');
  const [filterType, setFilterType] = useState(searchParams.get('type') || '');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [notes, setNotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);

  const emptyForm = {
    note_type: 'Credit Note', cn_dn_number: '', note_date: new Date().toISOString().split('T')[0],
    irn: '', eway_bill_no: '', amount: '', gst_amount: '', linked_invoice_id: '',
    linked_bill_id: '', linked_vo_id: '', reason: '', remarks: '',
  };
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => {
    axios.get(`${BACKEND}/api/projects?page=1&limit=200`, { withCredentials: true })
      .then(r => setProjects((r.data.projects || r.data).filter(p => p.status === 'Confirmed'))).catch(() => {});
  }, []);

  const fetchNotes = useCallback(async () => {
    if (!filterProject) { setNotes([]); setSummary(null); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('note_type', filterType);
      if (filterStatus) params.set('status', filterStatus);
      const [notesRes, summaryRes, invRes, billsRes] = await Promise.all([
        axios.get(`${BACKEND}/api/projects/${filterProject}/cn-dn?${params}`, { withCredentials: true }),
        axios.get(`${BACKEND}/api/projects/${filterProject}/cn-dn-summary`, { withCredentials: true }),
        axios.get(`${BACKEND}/api/projects/${filterProject}/tax-invoices`, { withCredentials: true }),
        axios.get(`${BACKEND}/api/projects/${filterProject}/bills`, { withCredentials: true }),
      ]);
      setNotes(notesRes.data);
      setSummary(summaryRes.data);
      setInvoices(invRes.data);
      setBills(billsRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [filterProject, filterType, filterStatus]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);
  useEffect(() => {
    const params = {};
    if (filterProject) params.project = filterProject;
    if (filterType) params.type = filterType;
    if (filterStatus) params.status = filterStatus;
    setSearchParams(params);
  }, [filterProject, filterType, filterStatus, setSearchParams]);

  const openCreate = () => { setEditingNote(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (note) => {
    setEditingNote(note);
    setForm({
      note_type: note.note_type, cn_dn_number: note.cn_dn_number,
      note_date: note.note_date ? note.note_date.split('T')[0] : '',
      irn: note.irn || '', eway_bill_no: note.eway_bill_no || '',
      amount: note.amount, gst_amount: note.gst_amount || '',
      linked_invoice_id: note.linked_invoice_id || '', linked_bill_id: note.linked_bill_id || '',
      linked_vo_id: note.linked_vo_id || '', reason: note.reason || '', remarks: note.remarks || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, project_id: filterProject,
      amount: parseFloat(form.amount) || 0, gst_amount: parseFloat(form.gst_amount) || 0,
      note_date: form.note_date ? new Date(form.note_date).toISOString() : null,
    };
    try {
      if (editingNote) {
        await axios.put(`${BACKEND}/api/cn-dn/${editingNote.id}`, payload, { withCredentials: true });
        toast.success('Updated');
      } else {
        await axios.post(`${BACKEND}/api/projects/${filterProject}/cn-dn`, payload, { withCredentials: true });
        toast.success('Created');
      }
      setDialogOpen(false); fetchNotes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleStatusChange = async (noteId, newStatus) => {
    try {
      await axios.put(`${BACKEND}/api/cn-dn/${noteId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`);
      setStatusDialog(null); fetchNotes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Status update failed'); }
  };

  const getNextStatuses = (current) => {
    const map = { 'Draft': ['Recorded'], 'Recorded': ['Linked', 'Draft'], 'Linked': ['Reconciled', 'Recorded'] };
    return map[current] || [];
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="cndn-back-btn">
            <ArrowLeft size={20} className="mr-2" /> Back
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900" data-testid="cndn-title">CN / DN Register</h1>
          <p className="text-sm text-zinc-600 mt-1">Credit Notes & Debit Notes — Recorded from Tally, linked & reconciled in ERP</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
            className="bg-white border border-zinc-200 rounded-none p-2.5 text-sm min-w-[260px]" data-testid="cndn-project-filter">
            <option value="">Select Project</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.pi_number} — {p.client_name}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="bg-white border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="cndn-type-filter">
            <option value="">All Types</option>
            <option value="Credit Note">Credit Note</option>
            <option value="Debit Note">Debit Note</option>
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="bg-white border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="cndn-status-filter">
            <option value="">All Statuses</option>
            {STATUS_FLOW.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {filterProject && (
            <Button onClick={openCreate} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none ml-auto" data-testid="cndn-add-btn">
              <Plus size={18} className="mr-1.5" /> New CN/DN
            </Button>
          )}
        </div>

        {!filterProject ? (
          <EmptyBox message="Select a project to view and manage Credit / Debit Notes" />
        ) : (
          <>
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-5" data-testid="cndn-summary">
                <SCard label="Credit Notes" value={fmtCur(summary.total_credit_notes)} sub={`${summary.cn_count} entries`} color="text-red-700" />
                <SCard label="Debit Notes" value={fmtCur(summary.total_debit_notes)} sub={`${summary.dn_count} entries`} color="text-green-700" />
                <SCard label="Net Adjustment" value={fmtCur(summary.net_adjustment)} color={summary.net_adjustment >= 0 ? 'text-green-700' : 'text-red-700'} />
                <SCard label="Unlinked" value={summary.unlinked_count} color={summary.unlinked_count > 0 ? 'text-amber-700' : 'text-green-700'} />
                <SCard label="Unreconciled" value={summary.unreconciled_count} color={summary.unreconciled_count > 0 ? 'text-amber-700' : 'text-green-700'} />
                <SCard label="Total Entries" value={summary.cn_count + summary.dn_count} />
              </div>
            )}

            {/* Invoice Adjustment Breakdown */}
            {summary && summary.invoice_adjustments && summary.invoice_adjustments.length > 0 && (
              <div className="bg-white border border-zinc-200 mb-5 overflow-x-auto" data-testid="invoice-adjustment-table">
                <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200">
                  <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Invoice-Level Adjustments</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50/50">
                    <tr>
                      {['Invoice #', 'Less: Credit Notes', 'Plus: Debit Notes', 'Net Adjustment'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {summary.invoice_adjustments.map((adj, i) => (
                      <tr key={i} className="border-b border-zinc-100">
                        <td className="px-3 py-2 text-xs font-mono text-zinc-700">{adj.invoice_number || adj.invoice_id}</td>
                        <td className="px-3 py-2 text-xs font-bold text-red-600">{adj.credit_notes > 0 ? `- ${fmtCur(adj.credit_notes)}` : '-'}</td>
                        <td className="px-3 py-2 text-xs font-bold text-green-600">{adj.debit_notes > 0 ? `+ ${fmtCur(adj.debit_notes)}` : '-'}</td>
                        <td className={`px-3 py-2 text-xs font-bold ${adj.net >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmtCur(adj.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Notes Table */}
            {loading ? <div className="text-center py-8 text-zinc-500">Loading...</div> : notes.length === 0 ? (
              <EmptyBox message="No Credit/Debit Notes found. Click 'New CN/DN' to create one." />
            ) : (
              <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="w-full text-sm" data-testid="cndn-table">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      {['Type', 'CN/DN #', 'Date', 'Amount', 'GST', 'Total', 'Invoice', 'Reason', 'Status', 'By', ''].map(h => (
                        <th key={h} className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {notes.map(n => (
                      <tr key={n.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`cndn-row-${n.id}`}>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 ${n.note_type === 'Credit Note' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {n.note_type === 'Credit Note' ? 'CN' : 'DN'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-xs font-mono font-bold text-zinc-800" data-testid={`cndn-number-${n.id}`}>{n.cn_dn_number}</td>
                        <td className="px-3 py-2.5 text-xs text-zinc-500">{fmtDate(n.note_date)}</td>
                        <td className="px-3 py-2.5 text-xs font-bold text-zinc-800 text-right">{fmtCur(n.amount)}</td>
                        <td className="px-3 py-2.5 text-xs text-zinc-500 text-right">{fmtCur(n.gst_amount)}</td>
                        <td className={`px-3 py-2.5 text-xs font-bold text-right ${n.note_type === 'Credit Note' ? 'text-red-600' : 'text-green-600'}`}>{fmtCur(n.total_amount)}</td>
                        <td className="px-3 py-2.5 text-xs text-zinc-600 max-w-[120px] truncate">{n.linked_invoice_number || '-'}</td>
                        <td className="px-3 py-2.5 text-xs text-zinc-600 max-w-[150px] truncate">{n.reason || '-'}</td>
                        <td className="px-3 py-2.5">
                          <button onClick={() => setStatusDialog(n)} className="cursor-pointer" data-testid={`cndn-status-${n.id}`}>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 ${STATUS_COLORS[n.status] || STATUS_COLORS['Draft']}`}>{n.status}</span>
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-[10px] text-zinc-400">{n.created_by_name}</td>
                        <td className="px-3 py-2.5">
                          {n.status !== 'Reconciled' && (
                            <button onClick={() => openEdit(n)} className="p-1 text-zinc-400 hover:text-blue-700" data-testid={`cndn-edit-${n.id}`}>
                              <NotePencil size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">{editingNote ? 'Edit' : 'New'} Credit / Debit Note</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-3">
            {/* Basic Info */}
            <fieldset className="border border-zinc-200 p-4">
              <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Basic Details</legend>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-zinc-500">Type *</Label>
                  <select value={form.note_type} onChange={e => setForm({ ...form, note_type: e.target.value })}
                    className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="cndn-form-type" disabled={!!editingNote}>
                    <option value="Credit Note">Credit Note</option>
                    <option value="Debit Note">Debit Note</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">CN/DN Number (from Tally) *</Label>
                  <Input value={form.cn_dn_number} onChange={e => setForm({ ...form, cn_dn_number: e.target.value })} required className="mt-1 rounded-none" data-testid="cndn-form-number" placeholder="e.g. SGD/CN/001" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Date</Label>
                  <Input type="date" value={form.note_date} onChange={e => setForm({ ...form, note_date: e.target.value })} className="mt-1 rounded-none" data-testid="cndn-form-date" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">IRN (optional)</Label>
                  <Input value={form.irn} onChange={e => setForm({ ...form, irn: e.target.value })} className="mt-1 rounded-none" data-testid="cndn-form-irn" placeholder="e-Invoice Reference" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-zinc-500">E-way Bill No (optional)</Label>
                  <Input value={form.eway_bill_no} onChange={e => setForm({ ...form, eway_bill_no: e.target.value })} className="mt-1 rounded-none" data-testid="cndn-form-eway" />
                </div>
              </div>
            </fieldset>

            {/* Financial */}
            <fieldset className="border border-zinc-200 p-4">
              <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Financial Details</legend>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-zinc-500">Amount (Pre-tax) *</Label>
                  <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required className="mt-1 rounded-none" data-testid="cndn-form-amount" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">GST Amount</Label>
                  <Input type="number" min="0" step="0.01" value={form.gst_amount} onChange={e => setForm({ ...form, gst_amount: e.target.value })} className="mt-1 rounded-none" data-testid="cndn-form-gst" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Total</Label>
                  <div className="mt-1 bg-zinc-100 border border-zinc-200 rounded-none p-2.5 text-sm font-bold" data-testid="cndn-form-total">
                    {fmtCur((parseFloat(form.amount) || 0) + (parseFloat(form.gst_amount) || 0))}
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Linkage */}
            <fieldset className="border border-zinc-200 p-4">
              <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Linkage</legend>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-xs text-zinc-500">Linked Invoice * (mandatory)</Label>
                  <select value={form.linked_invoice_id} onChange={e => setForm({ ...form, linked_invoice_id: e.target.value })} required
                    className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="cndn-form-invoice">
                    <option value="">Select Invoice</option>
                    {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.invoice_number} — {fmtCur(inv.invoice_value)}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Linked Bill (optional)</Label>
                  <select value={form.linked_bill_id} onChange={e => setForm({ ...form, linked_bill_id: e.target.value })}
                    className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="cndn-form-bill">
                    <option value="">None</option>
                    {bills.map(b => <option key={b.id} value={b.id}>{b.bill_number} — {fmtCur(b.bill_amount)}</option>)}
                  </select>
                </div>
              </div>
            </fieldset>

            {/* Reason */}
            <fieldset className="border border-zinc-200 p-4">
              <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Reason & Remarks</legend>
              <div>
                <Label className="text-xs text-zinc-500">Reason *</Label>
                <Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} required className="mt-1 rounded-none" data-testid="cndn-form-reason" placeholder="e.g. Rate difference, Scope reduction, Defect deduction" />
              </div>
              <div className="mt-3">
                <Label className="text-xs text-zinc-500">Remarks</Label>
                <textarea value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} rows={2}
                  className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm resize-none" data-testid="cndn-form-remarks" />
              </div>
            </fieldset>

            <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5" data-testid="cndn-form-submit">
              {editingNote ? 'Update' : 'Create'} {form.note_type}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Status Transition Dialog */}
      <Dialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-sm">
          <DialogHeader><DialogTitle className="text-lg font-bold">Update Status</DialogTitle></DialogHeader>
          {statusDialog && (
            <div className="space-y-3 mt-2">
              <div className="bg-zinc-50 border border-zinc-200 p-3">
                <p className="text-xs text-zinc-500">Current Status</p>
                <span className={`text-xs font-semibold px-2 py-0.5 ${STATUS_COLORS[statusDialog.status]}`}>{statusDialog.status}</span>
                <p className="text-xs text-zinc-700 mt-2 font-medium">{statusDialog.cn_dn_number} — {fmtCur(statusDialog.total_amount)}</p>
              </div>
              {getNextStatuses(statusDialog.status).length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500">Move to:</p>
                  {getNextStatuses(statusDialog.status).map(s => (
                    <Button key={s} onClick={() => handleStatusChange(statusDialog.id, s)}
                      variant="outline" className="w-full rounded-none border-zinc-300 justify-start text-sm" data-testid={`status-btn-${s.toLowerCase()}`}>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 mr-2 ${STATUS_COLORS[s]}`}>{s}</span>
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle size={20} /> <span className="text-sm font-medium">Fully reconciled. No further transitions.</span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EmptyBox = ({ message }) => (
  <div className="bg-white border border-zinc-200 p-12 text-center" data-testid="cndn-empty">
    <Receipt size={48} className="mx-auto text-zinc-300 mb-3" />
    <p className="text-sm text-zinc-500">{message}</p>
  </div>
);

const SCard = ({ label, value, sub, color = 'text-zinc-900' }) => (
  <div className="bg-white border border-zinc-200 px-3 py-2.5">
    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</p>
    <p className={`text-lg font-bold ${color}`}>{value}</p>
    {sub && <p className="text-[10px] text-zinc-400 mt-0.5">{sub}</p>}
  </div>
);

export default CreditDebitNoteRegister;
